import { useState } from 'react';
import { useSignIn, useSignUp } from '@clerk/clerk-react';

/**
 * Tela de login da plataforma (AMA Capital) - tema claro premium.
 * Split: painel de marca (gradiente esmeralda + logo) à esquerda + formulário
 * à direita. Só a camada visual mudou; a LÓGICA Clerk é a mesma.
 *
 * Fluxos (Clerk, custom flow):
 *  - entrar     → e-mail + senha (signIn.create) [+ 2FA quando exigido]
 *  - criar      → cadastro e-mail + senha (signUp.create)
 *  - verificar  → código de verificação do e-mail no cadastro
 *  - esqueci    → pede código de redefinição por e-mail
 *  - redefinir  → código + nova senha (attemptFirstFactor)
 *  - doisFatores→ segundo fator (totp / phone_code / backup_code)
 */

type Modo = 'entrar' | 'criar' | 'verificar' | 'esqueci' | 'redefinir' | 'doisFatores';

/** CTAs de "criar conta" (página de vendas) linkam para /entrar?modo=criar. */
function modoInicialDaUrl(): Modo {
  if (typeof window === 'undefined') return 'entrar';
  return new URLSearchParams(window.location.search).get('modo') === 'criar' ? 'criar' : 'entrar';
}
type Fator2 = 'totp' | 'phone_code' | 'backup_code';

const STATS: { valor: string; label: string }[] = [
  { valor: '−43%', label: 'taxa de distrato' },
  { valor: 'R$ 280k', label: 'preservados / 90d' },
  { valor: '312', label: 'contratos monitorados' },
];

function msgErro(err: unknown): string {
  const e = err as {
    errors?: { longMessage?: string; message?: string }[];
    message?: string;
  };
  return (
    e?.errors?.[0]?.longMessage ??
    e?.errors?.[0]?.message ??
    e?.message ??
    'Algo deu errado. Tente novamente.'
  );
}

const INPUT_CLS =
  'h-[46px] w-full rounded-xl border border-line-2 bg-surface px-[15px] text-sm text-ink outline-none focus:border-accent';
const LABEL_CLS = 'mb-2 block text-[12.5px] font-medium text-ink-2';

/** Máscara e validação BR do WhatsApp: (DDD) 9XXXX-XXXX. */
function mascaraWhatsapp(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : '';
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}
const whatsappValido = (v: string) => /^\(\d{2}\) 9\d{4}-\d{4}$/.test(v);

function IconeOlho({ aberto }: { aberto: boolean }) {
  return aberto ? (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
      <path
        d="M3 3l18 18M10.6 10.6a3 3 0 004.2 4.2M9.9 5.1A9.6 9.6 0 0112 5c6.5 0 10 7 10 7a17 17 0 01-3.3 4M6.1 6.1A17 17 0 002 12s3.5 7 10 7a9.6 9.6 0 003.9-.8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

/** Campo de senha com botão para mostrar/ocultar. */
function CampoSenha({
  label,
  value,
  onChange,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
}) {
  const [ver, setVer] = useState(false);
  return (
    <div>
      <label className={LABEL_CLS}>{label}</label>
      <div className="relative">
        <input
          type={ver ? 'text' : 'password'}
          required
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••••"
          className={`${INPUT_CLS} pr-11`}
        />
        <button
          type="button"
          onClick={() => setVer((v) => !v)}
          aria-label={ver ? 'Ocultar senha' : 'Mostrar senha'}
          className="absolute inset-y-0 right-0 flex items-center px-3.5 text-ink-3 hover:opacity-80"
        >
          <IconeOlho aberto={ver} />
        </button>
      </div>
    </div>
  );
}

const TITULOS: Record<Modo, { titulo: string; sub: string }> = {
  entrar: { titulo: 'Entrar na plataforma', sub: 'Acesse sua conta' },
  criar: { titulo: 'Criar acesso', sub: 'Cadastre-se para começar' },
  verificar: { titulo: 'Verifique seu e-mail', sub: 'Digite o código que enviamos' },
  esqueci: { titulo: 'Recuperar senha', sub: 'Enviaremos um código por e-mail' },
  redefinir: { titulo: 'Redefinir senha', sub: 'Código + nova senha' },
  doisFatores: { titulo: 'Verificação em duas etapas', sub: 'Digite o código do segundo fator' },
};

export function LoginPage() {
  const { isLoaded, signIn, setActive } = useSignIn();
  const { isLoaded: signUpLoaded, signUp, setActive: setActiveSignUp } = useSignUp();

  const [modo, setModo] = useState<Modo>(modoInicialDaUrl());
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [codigo, setCodigo] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [fator2, setFator2] = useState<Fator2>('totp');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  function irPara(m: Modo) {
    setErro(null);
    setModo(m);
  }

  async function submeter(e: React.FormEvent) {
    e.preventDefault();
    if (enviando) return;
    setErro(null);
    setEnviando(true);
    try {
      if (modo === 'entrar') {
        if (!isLoaded) return;
        let res = await signIn.create({ identifier: email.trim(), password: senha });
        if (res.status === 'needs_first_factor') {
          res = await signIn.attemptFirstFactor({ strategy: 'password', password: senha });
        }
        if (res.status === 'complete') {
          await setActive({ session: res.createdSessionId });
        } else if (res.status === 'needs_second_factor') {
          const sfs = res.supportedSecondFactors ?? [];
          const totp = sfs.find((f) => f.strategy === 'totp');
          const phone = sfs.find((f) => f.strategy === 'phone_code') as
            | { strategy: 'phone_code'; phoneNumberId: string }
            | undefined;
          if (totp) {
            setFator2('totp');
          } else if (phone) {
            await signIn.prepareSecondFactor({ strategy: 'phone_code', phoneNumberId: phone.phoneNumberId });
            setFator2('phone_code');
          } else {
            setFator2('backup_code');
          }
          setCodigo('');
          irPara('doisFatores');
        } else {
          console.error('[login] status inesperado:', res.status, res);
          setErro(`Login não concluído. Etapa: ${res.status}. (veja o console)`);
        }
      } else if (modo === 'doisFatores') {
        if (!isLoaded) return;
        const res = await signIn.attemptSecondFactor(
          fator2 === 'phone_code'
            ? { strategy: 'phone_code', code: codigo.trim() }
            : fator2 === 'backup_code'
              ? { strategy: 'backup_code', code: codigo.trim() }
              : { strategy: 'totp', code: codigo.trim() },
        );
        if (res.status === 'complete') await setActive({ session: res.createdSessionId });
        else setErro(`Não foi possível concluir. Etapa: ${res.status}.`);
      } else if (modo === 'criar') {
        if (!signUpLoaded) return;
        if (!whatsappValido(whatsapp)) {
          setErro('Informe um WhatsApp válido no formato (DDD) 9XXXX-XXXX.');
          return;
        }
        // WhatsApp viaja em unsafeMetadata pela etapa de verificação; é gravado
        // em org_member_perfil no boot (PerfilFlush), sem tela intermediária.
        await signUp.create({
          emailAddress: email.trim(),
          password: senha,
          unsafeMetadata: { whatsapp },
        });
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
        irPara('verificar');
      } else if (modo === 'verificar') {
        if (!signUpLoaded) return;
        const res = await signUp.attemptEmailAddressVerification({ code: codigo.trim() });
        if (res.status === 'complete') await setActiveSignUp({ session: res.createdSessionId });
        else setErro('Código inválido. Confira e tente de novo.');
      } else if (modo === 'esqueci') {
        if (!isLoaded) return;
        await signIn.create({
          strategy: 'reset_password_email_code',
          identifier: email.trim(),
        });
        irPara('redefinir');
      } else if (modo === 'redefinir') {
        if (!isLoaded) return;
        const res = await signIn.attemptFirstFactor({
          strategy: 'reset_password_email_code',
          code: codigo.trim(),
          password: novaSenha,
        });
        if (res.status === 'complete') await setActive({ session: res.createdSessionId });
        else setErro('Não foi possível redefinir. Verifique o código.');
      }
    } catch (err) {
      console.error('[login] erro:', err);
      setErro(msgErro(err));
    } finally {
      setEnviando(false);
    }
  }

  const botaoTexto: Record<Modo, string> = {
    entrar: enviando ? 'Entrando…' : 'Entrar',
    criar: enviando ? 'Criando…' : 'Criar acesso',
    verificar: enviando ? 'Verificando…' : 'Verificar e entrar',
    esqueci: enviando ? 'Enviando…' : 'Enviar código',
    redefinir: enviando ? 'Redefinindo…' : 'Redefinir e entrar',
    doisFatores: enviando ? 'Verificando…' : 'Verificar e entrar',
  };

  const { titulo, sub } = TITULOS[modo];

  return (
    <div className="grid min-h-screen bg-bg md:grid-cols-[1.08fr_0.92fr]">
      {/* ── APRESENTAÇÃO (mobile): mesma headline do painel de marca, empilhada
          ACIMA do formulário. Nada fica escondido no celular. ── */}
      <div
        className="px-6 pb-8 pt-10 text-white md:hidden"
        style={{ background: 'var(--brand-gradient)' }}
        data-testid="login-apresentacao-mobile"
      >
        <img src="/logo-ama-white.png" alt="AMA Capital" className="mb-6 block h-8 w-auto" />
        <h1 className="font-serif text-[28px] font-medium leading-[1.15] tracking-[-0.02em]">
          Menos distrato. Menos inadimplência.
          <br />
          <span className="text-white/60">Sem esforço manual.</span>
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-white/80">
          Agentes monitoram cada contrato, detectam o risco antes do problema e falam com o
          comprador na hora certa.
        </p>
        <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2">
          {STATS.map((s) => (
            <div key={s.label}>
              <span className="num font-serif text-lg font-medium">{s.valor}</span>{' '}
              <span className="text-xs text-white/70">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── PAINEL DE MARCA (desktop) ── */}
      <div
        className="relative hidden flex-col justify-between overflow-hidden p-[52px] text-white md:flex lg:px-[60px]"
        style={{ background: 'var(--brand-gradient)' }}
      >
        <div
          className="pointer-events-none absolute"
          style={{ top: -160, right: -120, width: 520, height: 520, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,.14), transparent 68%)' }}
        />
        <div
          className="pointer-events-none absolute"
          style={{ bottom: -200, left: -140, width: 460, height: 460, borderRadius: '50%', background: 'radial-gradient(circle, rgba(18,165,116,.35), transparent 70%)' }}
        />

        <div className="relative z-10">
          <img src="/logo-ama-white.png" alt="AMA Capital" className="block h-[52px] w-auto" />
        </div>

        <div className="relative z-10 max-w-[460px]">
          <div className="mb-6 text-[12.5px] font-semibold uppercase tracking-[0.14em] text-white/70">
            Plataforma de retenção pós-venda
          </div>
          <h1 className="font-serif text-[46px] font-medium leading-[1.08] tracking-[-0.022em]">
            Menos distrato.
            <br />
            Menos inadimplência.
            <br />
            <span className="text-white/55">Sem esforço manual.</span>
          </h1>
          <p className="mt-6 max-w-[430px] text-base leading-relaxed text-white/80">
            Agentes monitoram cada contrato, detectam o risco antes do problema e falam com o
            comprador na hora certa.
          </p>
        </div>

        <div className="relative z-10 flex gap-11">
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="num font-serif text-[30px] font-medium tracking-[-0.02em]">{s.valor}</div>
              <div className="mt-1.5 text-[12.5px] text-white/70">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FORMULÁRIO ── */}
      <div className="flex items-center justify-center bg-bg p-6 sm:p-12">
        <div className="w-full max-w-[392px]">
          <h2 className="text-[25px] font-semibold tracking-[-0.02em]">{titulo}</h2>
          <p className="mb-7 mt-1.5 text-sm text-ink-3">{sub}</p>

          <form onSubmit={submeter} className="space-y-[18px]">
            {(modo === 'entrar' || modo === 'criar' || modo === 'esqueci') && (
              <div>
                <label className={LABEL_CLS}>E-mail</label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@email.com"
                  className={INPUT_CLS}
                />
              </div>
            )}

            {(modo === 'entrar' || modo === 'criar') && (
              <CampoSenha
                label="Senha"
                value={senha}
                onChange={setSenha}
                autoComplete={modo === 'criar' ? 'new-password' : 'current-password'}
              />
            )}

            {modo === 'criar' && (
              <div>
                <label className={LABEL_CLS}>WhatsApp</label>
                <input
                  type="tel"
                  required
                  inputMode="numeric"
                  autoComplete="tel"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(mascaraWhatsapp(e.target.value))}
                  placeholder="(11) 91234-5678"
                  data-testid="signup-whatsapp"
                  className={INPUT_CLS}
                />
              </div>
            )}

            {(modo === 'verificar' || modo === 'redefinir' || modo === 'doisFatores') && (
              <div>
                <label className={LABEL_CLS}>
                  {modo === 'doisFatores'
                    ? fator2 === 'phone_code'
                      ? 'Código enviado por SMS'
                      : fator2 === 'backup_code'
                        ? 'Código de backup'
                        : 'Código do app autenticador'
                    : `Código enviado para ${email || 'seu e-mail'}`}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  required
                  autoComplete="one-time-code"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  placeholder="000000"
                  className={INPUT_CLS}
                />
              </div>
            )}

            {modo === 'redefinir' && (
              <CampoSenha label="Nova senha" value={novaSenha} onChange={setNovaSenha} autoComplete="new-password" />
            )}

            {erro && (
              <div
                className="rounded-xl px-3 py-2.5 text-xs"
                style={{ background: 'var(--risk-soft)', border: '1px solid var(--risk-line)', color: 'var(--risk)' }}
              >
                {erro}
              </div>
            )}

            {/* Alvo do CAPTCHA do Clerk (bot protection). Necessário em fluxos
                customizados - sem este elemento, o Clerk recusa com 422. */}
            <div id="clerk-captcha" />

            <button
              type="submit"
              disabled={enviando}
              className="btn btn-primary w-full disabled:opacity-60"
              style={{ height: 48 }}
            >
              {botaoTexto[modo]} <span className="text-base">→</span>
            </button>
          </form>

          {/* Navegação entre fluxos */}
          <div className="mt-[22px] text-sm">
            {modo === 'entrar' && (
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => irPara('esqueci')} className="text-ink-3 hover:underline">
                  Esqueceu a senha?
                </button>
                <button
                  type="button"
                  onClick={() => irPara('criar')}
                  className="font-semibold text-accent hover:underline"
                >
                  Criar acesso
                </button>
              </div>
            )}
            {modo === 'criar' && (
              <button type="button" onClick={() => irPara('entrar')} className="text-ink-3 hover:underline">
                Já tem conta? <span className="font-semibold text-accent">Entrar</span>
              </button>
            )}
            {(modo === 'esqueci' ||
              modo === 'redefinir' ||
              modo === 'verificar' ||
              modo === 'doisFatores') && (
              <button type="button" onClick={() => irPara('entrar')} className="text-ink-3 hover:underline">
                ← Voltar ao login
              </button>
            )}
          </div>

          {/* Nota LGPD */}
          <div className="mt-[30px] flex items-start gap-2.5 border-t border-line pt-[22px]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.7" className="mt-0.5 shrink-0">
              <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z" />
            </svg>
            <p className="text-xs leading-relaxed text-ink-3">
              Seus dados ficam isolados e privados, em conformidade com a LGPD.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
